import { getApiErrorMessage } from 'src/CrudManager/Core/Utils/apiError'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AxiosErrorResponse, ErrorDialogProps } from '../types'

/**
 * Internal composable for error message formatting and analysis.
 * Extracts error information from various error types (axios, network, etc.)
 * and provides formatted error messages, details, suggestions, and retry logic.
 *
 * @param props - ErrorDialog component props containing error, model context, and retry callback
 * @returns Object containing computed properties for error display:
 *   - `errorCode`: Extracted error code from axios response (e.g., 'MODEL_NOT_FOUND')
 *   - `errorMessage`: Formatted, localized error message for display
 *   - `errorDetails`: Additional error details from response (if available)
 *   - `suggestions`: Contextual suggestions based on error type
 *   - `showRetry`: Whether to show retry button based on error type and callback availability
 *
 * @internal This composable is internal to ErrorDialog and not part of the public API
 */
export function useErrorDialog(props: ErrorDialogProps) {
  const { t } = useI18n()

  /**
   * Extract error code from axios error response.
   *
   * @returns Error code string (e.g., 'MODEL_NOT_FOUND') or null if not available
   */
  const errorCode = computed(() => {
    if (!props.error) return null

    if ('response' in props.error && props.error.response) {
      const response = props.error.response as AxiosErrorResponse
      return response.data?.error_code || null
    }

    return null
  })

  /**
   * Format the main error message for display.
   * Handles various error types including axios errors, network errors, and generic errors.
   * Provides localized messages with context when available (model name, instance ID).
   *
   * @returns Localized error message string, or fallback to 'crudmanager.errors.unknown'
   */
  const errorMessage = computed(() => {
    if (!props.error) return t('crudmanager.errors.unknown')

    // Handle axios error response
    if ('response' in props.error && props.error.response) {
      const response = props.error.response as AxiosErrorResponse

      // Check for error code first
      if (response.data?.error_code === 'MODEL_NOT_FOUND') {
        return t('crudmanager.errors.modelNotFound', {
          model: props.modelName || t('crudmanager.errors.unknownModel'),
          id: props.instanceId || 'N/A',
        })
      }

      // Relay the backend message only when the status says the app authored it;
      // a 5xx body is uncaught exception text and may quote a third-party API.
      return getApiErrorMessage(props.error)
    }

    // Handle network errors
    const errorMsg = props.error.message || ''
    if (errorMsg.includes('Network Error') || errorMsg.includes('timeout')) {
      return t('crudmanager.errors.networkError')
    }

    // Default error message
    return errorMsg || t('crudmanager.errors.unknown')
  })

  /**
   * Extract error details from axios error response.
   * Provides additional context or description from the error response data.
   *
   * @returns Error description string from response data, or empty string if not available
   */
  const errorDetails = computed(() => {
    if (!props.error) return ''

    if ('response' in props.error && props.error.response) {
      const response = props.error.response as AxiosErrorResponse
      if (response.data?.description) {
        return response.data.description
      }
    }

    return ''
  })

  /**
   * Generate contextual suggestions based on error type.
   * Provides actionable suggestions to help users resolve common error scenarios:
   * - MODEL_NOT_FOUND: Check ID, refresh page
   * - Network errors: Check connection, retry
   * - 403/Unauthorized: Check permissions, contact admin
   * - 500/Server errors: Retry, contact support
   *
   * @returns Array of localized suggestion strings
   */
  const suggestions = computed(() => {
    const suggestionsList: string[] = []

    if (errorCode.value === 'MODEL_NOT_FOUND') {
      suggestionsList.push(t('crudmanager.errors.suggestions.checkId'))
      suggestionsList.push(t('crudmanager.errors.suggestions.refresh'))
    }

    const errorMsg = props.error?.message || ''

    if (errorMsg.includes('Network Error') || errorMsg.includes('timeout')) {
      suggestionsList.push(t('crudmanager.errors.suggestions.checkConnection'))
      suggestionsList.push(t('crudmanager.errors.suggestions.retry'))
    }

    if (errorMsg.includes('403') || errorMsg.includes('Unauthorized')) {
      suggestionsList.push(t('crudmanager.errors.suggestions.checkPermissions'))
      suggestionsList.push(t('crudmanager.errors.suggestions.contactAdmin'))
    }

    if (errorMsg.includes('500') || errorMsg.includes('Server Error')) {
      suggestionsList.push(t('crudmanager.errors.suggestions.retry'))
      suggestionsList.push(t('crudmanager.errors.suggestions.contactSupport'))
    }

    return suggestionsList
  })

  /**
   * Determine if retry action should be shown in the error dialog.
   * Retry is shown when:
   * - Error is a network error or timeout
   * - Error is a 500 server error
   * - An onRetry callback is provided via props
   *
   * @returns True if retry button should be displayed, false otherwise
   */
  const showRetry = computed(() => {
    if (!props.error) return false

    const errorMsg = props.error.message || ''
    return errorMsg.includes('Network Error') || errorMsg.includes('timeout') || errorMsg.includes('500') || !!props.onRetry
  })

  return {
    errorCode,
    errorMessage,
    errorDetails,
    suggestions,
    showRetry,
  } as const
}
